import * as React from "react";

const FLAGSMITH_API_URL = "https://api.flagsmith.com/api/v1/flags/";

/**
 * Flags committed to the codebase as the starting state. They are shown
 * until the Flagsmith API resolves; the API response is merged over them,
 * and they stay in place if the API is unreachable or errors.
 */
const DEFAULT_FLAGS = {
  show_carecatoperations: { enabled: false, value: null },
  show_bluesky: { enabled: false, value: null },
  // Contact form (RAM-1583) — off until Flagsmith resolves, so the form
  // never appears by accident if the API is unreachable.
  show_contactform: { enabled: false, value: null },
  website_contact_form_url: { enabled: false, value: null },
  website_contact_form_api_key: { enabled: false, value: null },
};

/** Returned by get_feature() when a key is neither in the API response
 *  nor in the default map (Flagsmith missing/unavailable). */
const NOT_FOUND = { enabled: false, value: "Not Found" };

const FlagsContext = React.createContext({
  flags: DEFAULT_FLAGS,
  get_feature: () => NOT_FOUND,
});

export function FlagsProvider({ children }) {
  const [flags, setFlags] = React.useState(DEFAULT_FLAGS);

  React.useEffect(() => {
    const environmentKey = process.env.GATSBY_FLAGSMITH_ENV_KEY;
    if (!environmentKey) {
      return undefined;
    }

    let cancelled = false;

    fetch(FLAGSMITH_API_URL, {
      headers: { "X-Environment-Key": environmentKey },
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Flagsmith responded with ${response.status}`);
        }
        return response.json();
      })
      .then((data) => {
        if (cancelled || !Array.isArray(data)) {
          return;
        }
        const fetched = {};
        for (const item of data) {
          if (item && item.feature && item.feature.name) {
            fetched[item.feature.name] = {
              enabled: Boolean(item.enabled),
              value: item.feature_state_value ?? null,
            };
          }
        }
        setFlags((current) => ({ ...current, ...fetched }));
      })
      .catch(() => {
        // Flagsmith missing/unreachable/invalid — keep the default flags.
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const get_feature = React.useCallback(
    (key) => flags[key] ?? NOT_FOUND,
    [flags]
  );

  const contextValue = React.useMemo(
    () => ({ flags, get_feature }),
    [flags, get_feature]
  );

  return (
    <FlagsContext.Provider value={contextValue}>
      {children}
    </FlagsContext.Provider>
  );
}

/** Access feature flags from anywhere inside <Layout>. */
export function useFlags() {
  return React.useContext(FlagsContext);
}
